import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { useAuthGuard } from '@components/AuthGuard';
import { ErrorFallbackView, ForbiddenView, NotFoundView } from '@components/ErrorView';
import {
  ROUTE_ABS_DOCUMENTS,
  ROUTE_ABS_GROUPER,
  ROUTE_ABS_INTEGRATIONS,
  ROUTE_ABS_MAIL,
  ROUTE_ABS_MANAGEMENT,
  ROUTE_ABS_MANAGEMENT_USERS_LIST,
  ROUTE_ABS_MCP,
} from '@constants/routes';
import * as Sentry from '@sentry/react';
import { TokenAttributesScopesEnum } from '@spazious/client-backoffice';

import { LayoutSplashScreen } from '../_metronic/layout';

const UserProfilePage = lazy(() => import('./modules/UserProfile/UserProfilePage'));
const ReservationsRoutes = lazy(() =>
  import('./modules/ECommerce/pages/reservations/ReservationsRoutes.jsx')
);
const CrmRoutes = lazy(() => import('./modules/ECommerce/pages/crm/CrmRoutes'));

const FinanceRoutes = lazy(() => import('./modules/ECommerce/pages/finance/FinanceRoutes.jsx'));

const ContentRoutes = lazy(() => import('./modules/ECommerce/pages/content/ContentRoutes'));
const DocumentsRoutes = lazy(() => import('./modules/ECommerce/pages/documents/DocumentsRoutes'));

const DiagramRoutes = lazy(() => import('./modules/ECommerce/pages/diagramming/DiagrammingRoutes'));
const ReportingRoutes = lazy(() => import('./modules/ECommerce/pages/reporting/ReportingRoutes'));
const SchedulersRoutes = lazy(() =>
  import('./modules/ECommerce/pages/schedulers/SchedulersRoutes')
);
const GrouperRoutes = lazy(() => import('./modules/ECommerce/pages/grouper/GrouperRoutes'));
const IntegrationsRoutes = lazy(() =>
  import('./modules/ECommerce/pages/integrations/IntegrationsRoutes')
);
const MailRoutes = lazy(() => import('./modules/ECommerce/pages/mail/MailRoutes'));
const McpGuidePage = lazy(() => import('./modules/ECommerce/pages/mcp/McpGuidePage'));
const ManagementRoutes = lazy(() =>
  import('./modules/ECommerce/pages/management/ManagementRoutes')
);

export default function BasePage() {
  const { hasScope } = useAuthGuard({ raiseError: false });
  const history = useHistory();
  const { pathname } = useLocation();

  // Reset the boundary when the top-level module changes, so navigating away
  // from a crashed page (e.g. via the sidebar) clears the fallback. Keyed by
  // the first path segment to avoid remounting on in-module sub-navigation.
  const moduleKey = pathname.split('/')[1] || 'root';

  let firstAvailableRoute = '';
  if (hasScope(TokenAttributesScopesEnum.ReadorderReporting)) {
    firstAvailableRoute = '/reporting';
  } else if (hasScope(TokenAttributesScopesEnum.ReadgenericOrders)) {
    firstAvailableRoute = '/reservations';
  } else if (hasScope(TokenAttributesScopesEnum.ReadvirtualMeetingRoomSetup)) {
    firstAvailableRoute = '/diagramming';
  } else if (hasScope(TokenAttributesScopesEnum.Readusers)) {
    firstAvailableRoute = ROUTE_ABS_MANAGEMENT_USERS_LIST;
  }

  return (
    <Sentry.ErrorBoundary
      key={moduleKey}
      fallback={({ resetError }) => (
        <ErrorFallbackView
          onReset={() => {
            history.push('/');
            resetError();
          }}
        />
      )}
    >
      <Suspense fallback={<LayoutSplashScreen />}>
        <Switch>
          <Route
            path="/reservations"
            render={(props) =>
              hasScope(TokenAttributesScopesEnum.ReadgenericOrders) ? (
                <ReservationsRoutes {...props} />
              ) : (
                <ForbiddenView />
              )
            }
          />

          <Route
            path="/crm"
            render={(props) =>
              hasScope(TokenAttributesScopesEnum.Readcrm) ? (
                <CrmRoutes {...props} />
              ) : (
                <ForbiddenView />
              )
            }
          />

          <Route component={DiagramRoutes} path="/diagramming" />

          <Route component={ReportingRoutes} path="/reporting" />

          <Route component={SchedulersRoutes} path="/schedulers" />

          <Route component={ContentRoutes} path="/content" />

          <Route component={DocumentsRoutes} path={ROUTE_ABS_DOCUMENTS} />

          <Route component={FinanceRoutes} path="/finance" />

          <Route component={UserProfilePage} path="/user-profile" />

          <Route component={GrouperRoutes} path={ROUTE_ABS_GROUPER} />

          <Route component={IntegrationsRoutes} path={ROUTE_ABS_INTEGRATIONS} />

          <Route exact component={McpGuidePage} path={ROUTE_ABS_MCP} />

          <Route
            path={ROUTE_ABS_MAIL}
            render={(props) =>
              hasScope(TokenAttributesScopesEnum.Reademails) ? (
                <MailRoutes {...props} />
              ) : (
                <ForbiddenView />
              )
            }
          />

          <Route component={ManagementRoutes} path={ROUTE_ABS_MANAGEMENT} />

          <Route
            exact
            path="/"
            render={() =>
              firstAvailableRoute ? <Redirect to={firstAvailableRoute} /> : <ForbiddenView />
            }
          />

          <Route component={NotFoundView} />
        </Switch>
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}
